import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Package, 
  Clock, 
  CheckCircle, 
  Truck, 
  XCircle,
  CreditCard,
  ChevronRight,
  DollarSign,
  ShoppingBag,
  TrendingUp
} from 'lucide-react';
import { ordersAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { getImageUrl, formatPrice } from '../utils/helpers';

const SellerOrders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSales: 0,
    pendingOrders: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    try {
      const data = await ordersAPI.getSales();
      setOrders(data);
      
      // Calculate stats
      const totalSales = data.length;
      const pendingOrders = data.filter(o => o.status === 'pending_payment' || o.status === 'paid').length;
      const totalRevenue = data.reduce((sum, o) => sum + (o.total || 0), 0);
      
      setStats({ totalSales, pendingOrders, totalRevenue });
    } catch (error) {
      console.error('Error loading sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending_payment: { 
        label: 'Pendiente de pago', 
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock
      },
      paid: { 
        label: 'Pagado - Por enviar', 
        color: 'bg-zl-orange-100 text-zl-orange-700',
        icon: CreditCard
      },
      shipped: { 
        label: 'Enviado', 
        color: 'bg-purple-100 text-purple-800',
        icon: Truck
      },
      delivered: { 
        label: 'Entregado', 
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle
      },
      cancelled: { 
        label: 'Cancelado', 
        color: 'bg-red-100 text-red-800',
        icon: XCircle
      }
    };
    return statusMap[status] || statusMap.pending_payment;
  };

  const handleMarkAsShipped = async (orderId) => {
    try {
      await ordersAPI.updateStatus(orderId, 'shipped');
      toast({
        title: '¡Pedido marcado como enviado!',
        description: 'El comprador ha sido notificado',
      });
      loadOrders();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="p-8 text-center">
          <p className="mb-4">Debes iniciar sesion para ver tus ventas</p>
          <Button onClick={() => navigate('/login')}>Iniciar sesion</Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-gray-300 animate-pulse mb-4" />
          <p className="text-gray-600">Cargando ventas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        <h1 className="text-3xl font-bold">Mis Ventas</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-zl-orange-100 rounded-full mr-4">
                <ShoppingBag className="w-6 h-6 text-zl-orange" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Ventas</p>
                <p className="text-2xl font-bold">{stats.totalSales}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-full mr-4">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pedidos Pendientes</p>
                <p className="text-2xl font-bold">{stats.pendingOrders}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full mr-4">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Ingresos Totales</p>
                <p className="text-2xl font-bold text-green-600">{formatPrice(stats.totalRevenue)}</p>
              </div>
            </div>
          </Card>
        </div>

        {orders.length === 0 ? (
          <Card className="p-12 text-center">
            <DollarSign className="w-24 h-24 mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold mb-2">No tienes ventas aún</h2>
            <p className="text-gray-600 mb-6">
              Publica productos para comenzar a vender
            </p>
            <Button onClick={() => navigate('/seller/products')} className="bg-zl-orange hover:bg-zl-orange-600">
              Ir a mis productos
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const statusInfo = getStatusInfo(order.status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <Card key={order.order_id} className="p-6">
                  {/* Order Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 pb-4 border-b">
                    <div>
                      <p className="text-sm text-gray-500">
                        Venta #{order.order_id?.substring(0, 8).toUpperCase()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    <Badge className={`${statusInfo.color} flex items-center gap-1 mt-2 md:mt-0`}>
                      <StatusIcon className="w-4 h-4" />
                      {statusInfo.label}
                    </Badge>
                  </div>

                  {/* Buyer Info */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-gray-700">Comprador:</p>
                    <p className="font-medium">{order.buyer_name}</p>
                    <p className="text-sm text-gray-600">{order.buyer_email}</p>
                    {order.shipping_address && (
                      <div className="mt-2 text-sm text-gray-600">
                        <p className="font-semibold text-gray-700">Enviar a:</p>
                        <p>{order.shipping_address.name}</p>
                        <p>{order.shipping_address.address}, {order.shipping_address.city}</p>
                        <p>Tel: {order.shipping_address.phone}</p>
                      </div>
                    )}
                  </div>

                  {/* Order Items */}
                  <div className="space-y-3 mb-4">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <img
                          src={getImageUrl(item.product_image)}
                          alt={item.product_title}
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/64'}
                        />
                        <div className="flex-1">
                          <p className="font-semibold">{item.product_title}</p>
                          {item.variant_label && <p className="text-sm text-gray-700">Variante: {item.variant_label}</p>}
                          <p className="text-sm text-gray-500">Cantidad: {item.quantity}</p>
                        </div>
                        <p className="font-bold">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    ))}
                  </div>

                  {/* Order Footer */}
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-4 border-t">
                    <div>
                      <p className="text-lg font-bold">
                        Total: <span className="text-green-600">{formatPrice(order.total)}</span>
                      </p>
                    </div>
                    
                    <div className="flex gap-2 mt-3 md:mt-0">
                      {order.status === 'paid' && (
                        <Button 
                          onClick={() => handleMarkAsShipped(order.order_id)}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Marcar como enviado
                        </Button>
                      )}

                      <Button 
                        variant="outline"
                        onClick={() => navigate(`/seller/orders/${order.order_id}`)}
                      >
                        Ver detalles
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
};

export default SellerOrders;
